import { useFormContext } from 'react-hook-form';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import FormFieldCalendar from '@/components/FormFieldCalendar';

import FormFieldPhone from '@/components/FormFieldPhone';
import FormFieldSearch from '@/components/FormFieldSearch';
import { FormFieldTimePicker } from '@/components/FormFieldTimePicker';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Search, X, BaggageClaim } from 'lucide-react';

import { useQuery } from '@tanstack/react-query';
import { genUniqueId } from '@/lib/utils';
import { getCounties, deliveryService, User } from '@/services/deliveryService';
import { DeliveryInfo } from '@/types/delivery.types';
import { useTeamGetAll } from '@/services/teamService';

interface DeliveryTab {
  id: string;
  label: string;
}

const searchDgrUsers = async (searchTerm: string): Promise<User[]> => {
  const result = await deliveryService.searchDgrUsers(searchTerm);
  return result.data || [];
};

// Custom hook for driver search to avoid Rules of Hooks violations
const useDeliveryDriverSearch = (params: { search?: string }) => {
  const searchTerm = params.search || '';
  const { data, isLoading } = useQuery({
    queryKey: ['delivery-drivers', searchTerm],
    queryFn: () => searchDgrUsers(searchTerm),
    enabled: true,
  });

  return {
    values: (data || []).map((u) => ({
      value: String(u.id || u.userId || ''),
      label: `${u.firstName} ${u.lastName}`,
      secondaryLabel: u.phone,
    })),
    isLoading,
  };
};

const DELIVERY_TEAM_KEY = 'DELIVERY';

const useTeamSearch = (params: { search?: string }) => {
  const searchTerm = params.search || undefined;
  const { data, isLoading } = useTeamGetAll({ search: searchTerm });

  return {
    values: (data?.teams || [])
      .filter((team) => team.key === DELIVERY_TEAM_KEY)
      .map((t) => ({
        value: t.id,
        label: t.name,
      })),
    isLoading,
  };
};

const useUserSearch = (params: { search?: string }) => {
  const searchTerm = params.search || '';
  const { data, isLoading } = useQuery({
    queryKey: ['users', searchTerm],
    queryFn: () => searchDgrUsers(searchTerm),
    enabled: true,
  });

  return {
    values: (data || []).map((u) => ({
      value: u.id || u.userId || '',
      label: `${u.firstName} ${u.lastName}`,
    })),
    isLoading,
  };
};

interface DeliveryInformationProps {
  allowPastDates?: boolean;
}

export const DeliveryInformation = ({ allowPastDates = false }: DeliveryInformationProps) => {
  const { control, setValue, watch, getValues } = useFormContext();

  // Get deliveries from form first to initialize tabs correctly
  const deliveries = (watch('deliveries') || []) as DeliveryInfo[];
  const clientId = watch('clientId') as string;
  const pickupDate = watch('pickupDate') as Date;
  const assignedPickupDriverId = watch('assignedPickupDriverId') as string;
  const pickupAddressSameAsClient = watch('pickupAddressSameAsClient') as boolean;

  const [deliveryTabs, setDeliveryTabs] = useState<DeliveryTab[]>(() => {
    // If we have persisted deliveries, create tabs to match them
    const currentDeliveries = getValues('deliveries') as DeliveryInfo[];
    if (currentDeliveries?.length > 0) {
      return currentDeliveries.map((d, i) => ({
        id: d.id,
        label: d.personEntityName || `Delivery ${i + 1}`
      }));
    }
    return [{ id: genUniqueId(), label: 'Delivery 1' }];
  });
  const [activeTab, setActiveTab] = useState(deliveryTabs[0].id);

  const SearchIconsProps = {
    Icon: Search,
    iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
  };

  // Fetch counties for dropdown
  const { data: countiesData } = useQuery({
    queryKey: ['counties'],
    queryFn: async () => {
      const result = await getCounties();
      return result.data || [];
    },
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });

  const countyOptions = (countiesData || []).map((county) => ({
    label: county.name,
    value: String(county.countyId),
  }));

  // Fetch client address
  const { data: clientData } = useQuery({
    queryKey: ['client-address', clientId],
    queryFn: async () => {
      if (!clientId) return null;
      const result = await deliveryService.getClientDetails(clientId);
      return result.data;
    },
    enabled: !!clientId,
  });

  // Initialize deliveries array if empty - only on first mount and if truly empty
  useEffect(() => {
    const currentDeliveries = getValues('deliveries') as DeliveryInfo[] | undefined;
    // Only initialize if deliveries is truly empty or undefined
    // This prevents overwriting existing data when navigating back in stepper
    if (!currentDeliveries || currentDeliveries.length === 0) {
      // Double-check that we don't have any delivery data in the form
      const hasExistingData = deliveryTabs.length > 1 ||
        (deliveryTabs.length === 1 && deliveryTabs[0].label !== 'Delivery 1');

      if (!hasExistingData) {
        setValue('deliveries', [
          {
            id: deliveryTabs[0].id,
            personEntityName: '',
            attention: null,
            phone: null,
            copyAddressFromClient: false,
            deliveryAddressLine1: '',
            deliveryAddressLine2: null,
            deliveryCity: '',
            deliveryCounty: '',
            deliveryState: '',
            deliveryZip: '',
            deliveryDate: pickupDate || new Date(),
            deliveryTime: '',
            assignedDeliveryDriverId: assignedPickupDriverId || null,
            deliveryDriverPhone: null,
            additionalDriverIds: [],
            returnDate: null, // Default to null (optional)
            hasReturn: false,
            deliveryInstructions: '',
            hasDgrCheck: false,
            checkNumber: null,
            checkAmount: null,
            assignedTeamId: null,
            assignedUserId: null,
          } as unknown as DeliveryInfo, // Type assertion as workaround until type file update succeeds
        ], { shouldValidate: false }); // Don't validate on init to avoid errors
      }
    }
  }, []); // Empty dependency array - only run once on mount

  const getCurrentDeliveryIndex = () => {
    const index = deliveries.findIndex((d) => d.id === activeTab);
    return index >= 0 ? index : 0; // Default to 0 if not found
  };

  const currentDeliveryIndex = getCurrentDeliveryIndex();

  const minDeliveryDate = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!pickupDate) return allowPastDates ? null : today;

    const pickupDateOnly = new Date(pickupDate);
    pickupDateOnly.setHours(0, 0, 0, 0);

    if (allowPastDates) return pickupDateOnly;

    return pickupDateOnly > today ? pickupDateOnly : today;
  })();

  // Sync tabs with deliveries when deliveries change (e.g., when navigating back with existing data)
  useEffect(() => {
    if (deliveries && deliveries.length > 0) {
      const currentTabs = deliveries.map((d, i) => ({
        id: d.id,
        label: d.personEntityName || `Delivery ${i + 1}`
      }));

      // Only update if tabs are different to avoid infinite loops
      const tabsChanged = currentTabs.length !== deliveryTabs.length ||
        currentTabs.some((tab, i) => tab.id !== deliveryTabs[i]?.id || tab.label !== deliveryTabs[i]?.label);

      if (tabsChanged) {
        setDeliveryTabs(currentTabs);
        // Ensure activeTab is valid
        if (!currentTabs.find(t => t.id === activeTab)) {
          setActiveTab(currentTabs[0].id);
        }
      }
    }
  }, [deliveries.length, deliveries.map(d => d.id).join(','), deliveries.map(d => d.personEntityName).join(',')]);

  // Watch for changes to person name to update tab label
  const personEntityName = watch(`deliveries.${currentDeliveryIndex}.personEntityName`) as string;
  useEffect(() => {
    if (personEntityName) {
      setDeliveryTabs((tabs) =>
        tabs.map((tab) => (tab.id === activeTab ? { ...tab, label: personEntityName } : tab))
      );
    }
  }, [personEntityName, activeTab]);

  // Auto-fill delivery driver from pickup driver
  useEffect(() => {
    if (assignedPickupDriverId) {
      setValue(`deliveries.${currentDeliveryIndex}.assignedDeliveryDriverId`, assignedPickupDriverId);
    }
  }, [assignedPickupDriverId, currentDeliveryIndex]);



  // Handle copy address from client
  const copyAddressFromClient = watch(`deliveries.${currentDeliveryIndex}.copyAddressFromClient`) as boolean;


  useEffect(() => {
    // Background sync: If checked, ensure fields are up to date from clientData/countiesData
    // ONLY if the fields are currently empty (prevents unwanted overwrites during loads)
    if (copyAddressFromClient && clientData) {
      const client = ((clientData as any)?.data || clientData || {}) as { address1?: string; address2?: string; city?: string; county?: string; state?: string; zipCode?: string };
      
      const currentAddr1Spec = getValues(`deliveries.${currentDeliveryIndex}.deliveryAddressLine1`);
      if (!currentAddr1Spec) {
        setValue(`deliveries.${currentDeliveryIndex}.deliveryAddressLine1`, client.address1 ?? '');
        setValue(`deliveries.${currentDeliveryIndex}.deliveryAddressLine2`, client.address2 ?? '');
        setValue(`deliveries.${currentDeliveryIndex}.deliveryCity`, client.city ?? '');
        setValue(`deliveries.${currentDeliveryIndex}.deliveryState`, client.state ?? '');
        setValue(`deliveries.${currentDeliveryIndex}.deliveryZip`, client.zipCode ?? '');

        if (countiesData) {
          const matchedCounty = countiesData?.find(c => c.name.toLowerCase() === client.county?.toLowerCase());
          setValue(`deliveries.${currentDeliveryIndex}.deliveryCounty`, String(matchedCounty?.countyId ?? ''));
        }
      }
    }
  }, [clientData, countiesData, currentDeliveryIndex, copyAddressFromClient]);

  // Fetch driver phone
  const assignedDeliveryDriverId = watch(`deliveries.${currentDeliveryIndex}.assignedDeliveryDriverId`) as string;
  const { data: driverData } = useQuery({
    queryKey: ['driver', assignedDeliveryDriverId],
    queryFn: async () => {
      if (!assignedDeliveryDriverId) return null;
      const result = await deliveryService.getUserDetails(assignedDeliveryDriverId);
      return result.data;
    },
    enabled: !!assignedDeliveryDriverId,
  });

  useEffect(() => {
    if (driverData) {
      setValue(`deliveries.${currentDeliveryIndex}.deliveryDriverPhone`, driverData.phone || null);
    }
  }, [driverData, currentDeliveryIndex]);

  // Keep primary and additional drivers unique and remove duplicate additional drivers.
  useEffect(() => {
    deliveries.forEach((delivery, index) => {
      const primaryDriverId = delivery?.assignedDeliveryDriverId ? String(delivery.assignedDeliveryDriverId) : '';
      const additionalDriverIds = (delivery?.additionalDriverIds || []).map((id) => String(id || ''));

      const dedupedAdditionalDriverIds = Array.from(new Set(additionalDriverIds.filter(Boolean)));
      const filteredAdditionalDriverIds = primaryDriverId
        ? dedupedAdditionalDriverIds.filter((id) => id !== primaryDriverId)
        : dedupedAdditionalDriverIds;

      const hasChanged =
        filteredAdditionalDriverIds.length !== additionalDriverIds.length ||
        filteredAdditionalDriverIds.some((id, driverIndex) => id !== additionalDriverIds[driverIndex]);

      if (hasChanged) {
        setValue(`deliveries.${index}.additionalDriverIds`, filteredAdditionalDriverIds, { shouldValidate: true });
      }
    });
  }, [
    deliveries.map((delivery) => delivery.id).join(','),
    deliveries.map((delivery) => delivery.assignedDeliveryDriverId || '').join(','),
    deliveries.map((delivery) => (delivery.additionalDriverIds || []).join('|')).join(','),
  ]);

  // Sync return date with delivery date when hasReturn is checked
  const currentDeliveryDate = watch(`deliveries.${currentDeliveryIndex}.deliveryDate`) as Date | null;
  const currentHasReturn = watch(`deliveries.${currentDeliveryIndex}.hasReturn`) as boolean;
  const currentReturnDate = watch(`deliveries.${currentDeliveryIndex}.returnDate`) as Date | null;
  useEffect(() => {
    if (currentHasReturn && currentDeliveryDate && currentReturnDate) {
      const delivDateClean = new Date(currentDeliveryDate);
      delivDateClean.setHours(0, 0, 0, 0);
      const retDateClean = new Date(currentReturnDate);
      retDateClean.setHours(0, 0, 0, 0);
      // Only auto-sync if return date is before the new delivery date
      if (retDateClean < delivDateClean) {
        setValue(`deliveries.${currentDeliveryIndex}.returnDate`, delivDateClean);
      }
    }
  }, [currentDeliveryDate, currentHasReturn, currentDeliveryIndex]);

  const handleAddDelivery = () => {
    const newId = genUniqueId();
    const newDelivery = {
      id: newId,
      personEntityName: '',
      attention: null,
      phone: null,
      copyAddressFromClient: false,
      deliveryAddressLine1: '',
      deliveryAddressLine2: null,
      deliveryCity: '',
      deliveryCounty: '',
      deliveryState: '',
      deliveryZip: '',
      deliveryDate: pickupDate || new Date(),
      deliveryTime: '',
      assignedDeliveryDriverId: assignedPickupDriverId || null,
      deliveryDriverPhone: null,
      additionalDriverIds: [],
      returnDate: null,
      hasReturn: false,
      deliveryInstructions: '',
      hasDgrCheck: false,
      checkNumber: null,
      checkAmount: null,
      assignedTeamId: null,
      assignedUserId: null,
    };

    setDeliveryTabs([...deliveryTabs, { id: newId, label: `Delivery ${deliveryTabs.length + 1}` }]);
    setValue('deliveries', [...deliveries, newDelivery]);
    setActiveTab(newId);
  };

  const handleRemoveDelivery = (tabId: string) => {
    if (deliveryTabs.length === 1) return; // Don't remove last delivery

    const updatedTabs = deliveryTabs.filter((tab) => tab.id !== tabId);
    const updatedDeliveries = deliveries.filter((d) => d.id !== tabId);

    setDeliveryTabs(updatedTabs);
    setValue('deliveries', updatedDeliveries);

    if (activeTab === tabId) {
      setActiveTab(updatedTabs[0].id);
    }
  };

  const handleAddAdditionalDriver = (index: number) => {
    const currentList = (deliveries[index]?.additionalDriverIds) ?? [];
    setValue(`deliveries.${index}.additionalDriverIds`, [...currentList, '']);
  };

  const handleRemoveAdditionalDriver = (deliveryIndex: number, driverIndex: number) => {
    const currentList = (deliveries[deliveryIndex]?.additionalDriverIds) ?? [];
    const updated = currentList.filter((_, i) => i !== driverIndex);
    setValue(`deliveries.${deliveryIndex}.additionalDriverIds`, updated);
  };

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      {/* Delivery Tabs - Horizontal Scrollable Header */}
      <div className="bg-[#F5F5F5] h-12 flex overflow-x-scroll overflow-y-clip scrollbar-hide">
        {deliveryTabs.map((tab, tabIndex) => {
          const activeTabIndex = deliveryTabs.findIndex((deliveryTab) => deliveryTab.id === activeTab);
          const isActiveTab = tabIndex === activeTabIndex;
          return (
            <div
              key={`${tab.id}-${tabIndex}`}
              onClick={() => setActiveTab(tab.id)}
              className={`p-2 flex min-w-fit flex-row cursor-pointer align-middle items-center justify-between gap-2 rounded-none hover:none text-primary ${isActiveTab ? 'bg-[#EDF5FF] text-primary' : 'bg-[#E2E2E2] text-primary cursor-pointer'
                }`}
            >
              <BaggageClaim color="#0A2240" />
              {tab.label.length > 20 ? `${tab.label.slice(0, 20)}...` : tab.label}

              {deliveryTabs.length > 1 && isActiveTab && (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveDelivery(tab.id);
                  }}
                >
                  <Trash2 color="#D72222" className="w-5 h-5" />
                </div>
              )}
            </div>
          );
        })}
        <Button
          onClick={handleAddDelivery}
          className="p-2 bg-primary h-12 w-12 text-white flex justify-center align-middle rounded-none"
          type="button"
        >
          <Plus />
        </Button>
      </div>

      {/* Delivery Form Content */}
      {deliveryTabs.map((tab) => {
        // Find the actual index of this delivery in the deliveries array
        const deliveryIndex = deliveries.findIndex((d) => d.id === tab.id);
        if (deliveryIndex === -1 || activeTab !== tab.id) return null; // Only show active tab content

        // Get delivery data for conditional rendering
        const delivery = deliveries[deliveryIndex];
        const hasDgrCheck = delivery?.hasDgrCheck ?? false;

        return (
          <div key={tab.id} className="flex flex-col gap-6 mt-6">
            {/* Row 1: Person/Entity Name, Attention, Phone (equal thirds) */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-4">
                <FormFieldComponent
                  control={control}
                  name={`deliveries.${deliveryIndex}.personEntityName`}
                  label="Person/Entity's Name"
                  placeholder="Enter Name"
                  replaceRegex={/[^a-zA-Z\s\-'.]/g}
                  required
                />
              </div>
              <div className="md:col-span-4">
                <FormFieldComponent
                  control={control}
                  name={`deliveries.${deliveryIndex}.attention`}
                  label="Attention"
                  placeholder="Enter Name"
                  replaceRegex={/[^a-zA-Z\s\-'.]/g}
                />
              </div>
              <div className="md:col-span-4">
                <FormFieldPhone name={`deliveries.${deliveryIndex}.phone`} label="Phone Number" placeholder="Enter Phone Number" />
              </div>
            </div>

            {/* Row 2: Copy Address Checkbox */}
            <div className="grid grid-cols-1 gap-6">
              <FormFieldCheckbox
                control={control}
                name={`deliveries.${deliveryIndex}.copyAddressFromClient`}
                label="Delivery Address is Same as Client's Office"
                description="" // Removed description to match design
                disabled={pickupAddressSameAsClient}
                onCheckChange={(checked) => {
                  if (checked && clientData) {
                    const client = ((clientData as any)?.data || clientData || {}) as any;
                    setValue(`deliveries.${deliveryIndex}.deliveryAddressLine1`, client.address1 ?? '', { shouldValidate: true });
                    setValue(`deliveries.${deliveryIndex}.deliveryAddressLine2`, client.address2 ?? '');
                    setValue(`deliveries.${deliveryIndex}.deliveryCity`, client.city ?? '', { shouldValidate: true });
                    setValue(`deliveries.${deliveryIndex}.deliveryState`, client.state ?? '', { shouldValidate: true });
                    setValue(`deliveries.${deliveryIndex}.deliveryZip`, client.zipCode ?? '', { shouldValidate: true });

                    if (countiesData) {
                      const matchedCounty = countiesData?.find(c => c.name.toLowerCase() === client.county?.toLowerCase());
                      setValue(`deliveries.${deliveryIndex}.deliveryCounty`, String(matchedCounty?.countyId ?? ''), { shouldValidate: true });
                    }
                  } else if (!checked) {
                    setValue(`deliveries.${deliveryIndex}.deliveryAddressLine1`, '', { shouldValidate: true });
                    setValue(`deliveries.${deliveryIndex}.deliveryAddressLine2`, '');
                    setValue(`deliveries.${deliveryIndex}.deliveryCity`, '', { shouldValidate: true });
                    setValue(`deliveries.${deliveryIndex}.deliveryCounty`, '', { shouldValidate: true });
                    setValue(`deliveries.${deliveryIndex}.deliveryState`, '', { shouldValidate: true });
                    setValue(`deliveries.${deliveryIndex}.deliveryZip`, '', { shouldValidate: true });
                  }
                }}
              />
            </div>

            {/* Row 3: Address Line 1 */}
            <div className="grid grid-cols-1 gap-6">
              <FormFieldComponent
                control={control}
                name={`deliveries.${deliveryIndex}.deliveryAddressLine1`}
                label="Address Line 1"
                placeholder="Enter Street Address"
                required
              />
            </div>

            {/* Row 4: Address Line 2 (2/3) and City (1/3) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <FormFieldComponent
                  control={control}
                  name={`deliveries.${deliveryIndex}.deliveryAddressLine2`}
                  label="Address Line 2"
                  placeholder="Enter Apt, suite etc. (Optional)"
                />
              </div>
              <div className="md:col-span-1">
                <FormFieldComponent
                  control={control}
                  name={`deliveries.${deliveryIndex}.deliveryCity`}
                  label="City"
                  placeholder="City"
                  required
                />
              </div>
            </div>

            {/* Row 5: County, State, Zip Code */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormFieldSelect
                name={`deliveries.${deliveryIndex}.deliveryCounty`}
                label="County"
                required
                options={countyOptions}
                placeholder="Select County"
              />
              <FormFieldComponent
                control={control}
                name={`deliveries.${deliveryIndex}.deliveryState`}
                label="State"
                placeholder="State"
                required
                allowNumber={false}
              />
              <FormFieldComponent
                control={control}
                name={`deliveries.${deliveryIndex}.deliveryZip`}
                label="Zip Code"
                placeholder="Zip Code"
                required
              />
            </div>

            {/* Row 6: Delivery Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormFieldCalendar
                name={`deliveries.${deliveryIndex}.deliveryDate`}
                label="Delivery Date"
                required
                disabledAt={(date) => {
                  if (!minDeliveryDate) return false;
                  const candidateDate = new Date(date);
                  candidateDate.setHours(0, 0, 0, 0);
                  return candidateDate < minDeliveryDate;
                }}
              />
              <FormFieldTimePicker
                name={`deliveries.${deliveryIndex}.deliveryTime`}
                label="Delivery Time"
                required
                timeFields={['H', 'M']}
                use12HourFormat
                returnFormattedValue
                placeHolder="Enter Time"
              />
            </div>

            {/* Row 7: Assigned Delivery Driver and Driver's Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end relative">
              <FormFieldSearch
                name={`deliveries.${deliveryIndex}.assignedDeliveryDriverId`}
                label="Assign Delivery Driver"
                placeholder="Search Delivery Driver"
                props={SearchIconsProps}
                api={(params) => useDeliveryDriverSearch({ search: params.search || '' })}
              />
              <FormFieldComponent
                control={control}
                name={`deliveries.${deliveryIndex}.deliveryDriverPhone`}
                label="Driver's Phone Number"
                placeholder="Driver's Phone Number"
                disabled
              />
            </div>

            {/* Additional Drivers Section */}
            <div className="flex flex-col gap-4">
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:text-primary/90 font-medium"
                  onClick={() => handleAddAdditionalDriver(deliveryIndex)}
                >
                  + Add Driver
                </Button>
              </div>

              {delivery.additionalDriverIds?.map((_: string, driverIndex: number) => (
                <div key={driverIndex} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center border p-4 rounded-md relative mt-2">
                  <FormFieldSearch
                    name={`deliveries.${deliveryIndex}.additionalDriverIds.${driverIndex}`}
                    label={`Additional Driver ${driverIndex + 1}`}
                    placeholder="Search Driver"
                    props={SearchIconsProps}
                    api={(params) => {
                      const result = useDeliveryDriverSearch({ search: params.search ?? '' });
                      // Filter out primary driver and other already-selected additional drivers
                      const primaryDriverId = delivery?.assignedDeliveryDriverId ? String(delivery.assignedDeliveryDriverId) : '';
                      const selectedAdditionalIds = (delivery?.additionalDriverIds ?? [])
                        .filter((_: string, i: number) => i !== driverIndex)
                        .map((id) => String(id || ''));
                      const excludeIds = new Set([primaryDriverId, ...selectedAdditionalIds].filter(Boolean));
                      return {
                        ...result,
                        values: result.values.filter((v) => !excludeIds.has(String(v.value))),
                      };
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAdditionalDriver(deliveryIndex, driverIndex)}
                    className="absolute top-2 right-2 h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Row 8: Instructions */}
            <div className="grid grid-cols-1 gap-6">
              <FormFieldComponent
                control={control}
                name={`deliveries.${deliveryIndex}.deliveryInstructions`}
                label="Instructions"
                placeholder="Enter Instructions"
                required
              />
            </div>

            {/* Row 9 & 10: Return Required Checkbox & Date */}
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center gap-2 mt-2">
                <FormFieldCheckbox
                  control={control}
                  name={`deliveries.${deliveryIndex}.hasReturn`}
                  label="Return Required"
                  onCheckChange={(checked) => {
                    if (checked) {
                      const deliveryDate = getValues(`deliveries.${deliveryIndex}.deliveryDate`) as Date | null;
                      const referenceDate = deliveryDate || pickupDate;
                      if (referenceDate) {
                        const cleanDate = new Date(referenceDate);
                        cleanDate.setHours(0, 0, 0, 0);
                        setValue(`deliveries.${deliveryIndex}.returnDate`, cleanDate);
                      }
                    } else {
                      setValue(`deliveries.${deliveryIndex}.returnDate`, null);
                    }
                  }}
                  description=""
                />
              </div>
              {watch(`deliveries.${deliveryIndex}.returnDate`) && (
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <FormFieldCalendar
                    control={control}
                    name={`deliveries.${deliveryIndex}.returnDate`}
                    label="Return Date"
                    disablePastDates
                    required={false}
                  />
                </div>
              )}
            </div>

            {/* Row 11 & 12: DGR Check & Details */}
            <div className="grid grid-cols-1 gap-4 mt-2">
              <FormFieldCheckbox
                control={control}
                name={`deliveries.${deliveryIndex}.hasDgrCheck`}
                label="DGR Check"
                description=""
                onCheckChange={(checked) => {
                  if (!checked) {
                    setValue(`deliveries.${deliveryIndex}.checkNumber`, null);
                    setValue(`deliveries.${deliveryIndex}.checkAmount`, null);
                  }
                }}
              />
              {hasDgrCheck && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormFieldComponent
                    control={control}
                    name={`deliveries.${deliveryIndex}.checkNumber`}
                    label="Check #"
                    placeholder="Enter Check Number"
                    maxLength={20}
                    replaceRegex={/\D/g}
                    props={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    required
                  />
                  <FormFieldComponent
                    control={control}
                    name={`deliveries.${deliveryIndex}.checkAmount`}
                    label="Amount"
                    placeholder="Enter Amount"
                    type="number"
                    props={{ step: '1.00' }}
                    required
                  />
                </div>
              )}
            </div>

            {/* Row 13: Assign Team & Assign User */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
              <FormFieldSearch
                name={`deliveries.${deliveryIndex}.assignedTeamId`}
                label="Assign Team"
                placeholder="Search Team"
                props={SearchIconsProps}
                api={(params) => useTeamSearch({ search: params.search || '' })}
              />
              <FormFieldSearch
                name={`deliveries.${deliveryIndex}.assignedUserId`}
                label="Assign User"
                placeholder="Search User"
                props={SearchIconsProps}
                api={(params) => useUserSearch({ search: params.search || '' })}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
